import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Phone, Sparkles } from 'lucide-react';
import axios from 'axios';
import { WHATSAPP_NUMBER, CONTACT_PHONE_DISPLAY, chatBotReplies } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WA_STORAGE_KEY = 'wbs_livechat_v4';
const AI_STORAGE_KEY = 'wbs_ai_chat_v1';
const WA_SESSION_KEY = 'wbs_livechat_session';
const AI_SESSION_KEY = 'wbs_ai_session';

function getSessionId(k) {
  try {
    let s = localStorage.getItem(k);
    if (!s) {
      s = `wbs-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
      localStorage.setItem(k, s);
    }
    return s;
  } catch (e) {
    return `wbs-${Date.now()}`;
  }
}

const whatsappHref = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text || '')}`;

const WhatsAppIcon = ({ size = 22 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.199-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347zM12.037 21.5h-.008a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.822 9.822 0 0 1 2.894 6.994c-.003 5.45-4.437 9.885-9.882 9.885zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.492-8.7z" />
  </svg>
);

// ============================================================
// WHATSAPP WIDGET (bottom-left)
// ============================================================
const WhatsAppWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sessionId] = useState(() => getSessionId(WA_SESSION_KEY));
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(WA_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) { /* empty */ }
    return [
      { from: 'bot', text: chatBotReplies[0], time: Date.now() },
      { from: 'bot', text: 'Type your message. When you press send, we will open WhatsApp so our admissions team can continue the conversation with you.', time: Date.now() },
    ];
  });
  const listRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(WA_STORAGE_KEY, JSON.stringify(messages));
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const persist = async (text, sender = 'user') => {
    try {
      await axios.post(`${API}/chats`, { session_id: sessionId, text, sender });
    } catch (e) { /* silent */ }
  };

  const send = (text) => {
    const t = (text ?? input).trim();
    if (!t) return;
    setMessages((m) => [...m, { from: 'user', text: t, time: Date.now() }]);
    setInput('');
    persist(t, 'user');
    const wa = whatsappHref(`Hello Whiteplains British Schools, ${t}`);
    setTimeout(() => window.open(wa, '_blank', 'noopener,noreferrer'), 250);
    setTimeout(() => {
      const ack = 'We have opened WhatsApp for you. Please send the message there — our admissions team responds fastest on WhatsApp during working hours.';
      setMessages((m) => [...m, { from: 'bot', text: ack, time: Date.now() }]);
      persist(ack, 'bot');
    }, 800);
  };

  const quickReplies = ['I want to enrol my child', 'What are your fees?', 'Book a campus tour', 'Ask about programmes'];

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.5, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 left-6 z-40 group flex items-center gap-3 rounded-full bg-[#25D366] text-white shadow-2xl border border-white/25 pl-3 pr-4 py-3 pulse-ring"
        aria-label="Chat with a human on WhatsApp"
      >
        <span className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          {open ? <X size={20} /> : <WhatsAppIcon size={20} />}
        </span>
        <span className="text-left leading-tight">
          <span className="block text-[9px] uppercase tracking-[0.28em] opacity-90">Chat with a Human</span>
          <span className="block text-[13px] font-semibold">on WhatsApp</span>
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 left-4 md:left-6 z-40 w-[calc(100vw-2rem)] max-w-sm bg-white shadow-2xl overflow-hidden border border-[#eadfc9]"
          >
            <div className="bg-[#075E54] p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#25D366] grid place-items-center border-2 border-white/20">
                  <WhatsAppIcon size={20} />
                </div>
                <div className="flex-1">
                  <div className="font-serif-display font-black text-[15px] tracking-wide leading-tight">Chat with a Human · WhatsApp</div>
                  <div className="text-[11px] flex items-center gap-2 text-white/80 mt-0.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-300" /> Our admissions team is online
                  </div>
                </div>
                <a
                  href={whatsappHref('Hello Whiteplains British Schools')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-sans-pro uppercase tracking-[0.2em] bg-white/15 hover:bg-white/25 px-2.5 py-1.5 transition-colors flex items-center gap-1.5"
                >
                  <Phone size={12} /> Call
                </a>
              </div>
              <div className="mt-3 text-[11px] text-white/80 flex items-center gap-2">
                Typically replies in a few minutes · <span className="font-medium text-white">{CONTACT_PHONE_DISPLAY}</span>
              </div>
            </div>
            <div
              ref={listRef}
              className="h-72 overflow-y-auto p-4 space-y-3"
              style={{
                background: '#ECE5DD',
                backgroundImage: 'radial-gradient(rgba(0,0,0,0.03) 1px, transparent 1px)',
                backgroundSize: '10px 10px',
              }}
            >
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-3.5 py-2 text-[13.5px] leading-snug shadow-sm rounded-lg ${m.from === 'user' ? 'bg-[#DCF8C6] text-[#1c1a17] rounded-br-sm' : 'bg-white text-[#1c1a17] rounded-bl-sm'}`}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2 bg-[#F0EDE5] border-t border-[#e6e0d3]">
              {quickReplies.map((q) => (
                <button key={q} onClick={() => send(q)} className="text-[11px] font-sans-pro text-[#075E54] bg-white px-3 py-1.5 border border-[#d7d0bf] hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors rounded-full">
                  {q}
                </button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 bg-[#F0EDE5] border-t border-[#e6e0d3]">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 bg-white border border-[#d7d0bf] px-4 py-2.5 text-sm rounded-full outline-none focus:border-[#25D366] transition-colors"
              />
              <button type="submit" className="w-11 h-11 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white grid place-items-center transition-colors" aria-label="send">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============================================================
// AI CHATBOT WIDGET (bottom-right)
// ============================================================
const AiWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => getSessionId(AI_SESSION_KEY));
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(AI_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) { /* empty */ }
    return [
      { from: 'ai', text: 'Hello, I\u2019m Maxie \u2014 the AI concierge for Whiteplains British School. Ask me anything about admissions, programmes, our VR gaming room, Mr Johnson Okeke (AI teacher) or the WBS Microfinance Bank.', time: Date.now() },
    ];
  });
  const listRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(AI_STORAGE_KEY, JSON.stringify(messages));
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { from: 'user', text: t, time: Date.now() }]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/ai/chat`, { session_id: sessionId, message: t });
      setMessages((m) => [...m, { from: 'ai', text: res.data?.reply || '…', time: Date.now() }]);
    } catch (e) {
      setMessages((m) => [...m, {
        from: 'ai',
        text: 'Sorry, I\u2019m having trouble responding right now. Please try again in a moment, or chat with a human on WhatsApp (' + CONTACT_PHONE_DISPLAY + ').',
        time: Date.now(),
      }]);
    } finally { setSending(false); }
  };

  const quickReplies = ['Tell me about admissions', 'What is the VR gaming room?', 'Who is Mr Johnson Okeke?', 'What is WBS Microfinance Bank?'];

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-3 rounded-full bg-[#0f0d0b] text-white shadow-2xl border border-[#c8942a] pl-4 pr-3 py-3 pulse-ring"
        aria-label="Chat with the AI assistant"
      >
        <span className="text-right leading-tight">
          <span className="block text-[9px] uppercase tracking-[0.28em] text-[#f0c95a]">Chat with AI</span>
          <span className="block text-[13px] font-semibold">Ask Maxie</span>
        </span>
        <span className="w-9 h-9 rounded-full bg-[#c8942a]/20 border border-[#c8942a] grid place-items-center">
          {open ? <X size={20} /> : <Sparkles size={20} className="text-[#f0c95a]" />}
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-4 md:right-6 z-40 w-[calc(100vw-2rem)] max-w-sm bg-white shadow-2xl overflow-hidden border border-[#eadfc9]"
          >
            <div className="bg-[#0f0d0b] p-4 text-white border-b border-[#c8942a]/40">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#c8942a]/20 border border-[#c8942a] grid place-items-center">
                  <Sparkles size={18} className="text-[#f0c95a]" />
                </div>
                <div className="flex-1">
                  <div className="font-serif-display font-black text-[15px] tracking-wide leading-tight">Maxie · AI Concierge</div>
                  <div className="text-[11px] flex items-center gap-2 text-white/70 mt-0.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#f0c95a] animate-pulse" />
                    Powered by AI · replies instantly
                  </div>
                </div>
              </div>
            </div>
            <div ref={listRef} className="h-72 overflow-y-auto p-4 space-y-3 bg-[#fbf7f2]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[82%] px-3.5 py-2.5 text-[13.5px] leading-snug shadow-sm ${m.from === 'user' ? 'bg-[#0f0d0b] text-white rounded-lg rounded-br-sm' : 'bg-white text-[#1c1a17] border border-[#eadfc9] rounded-lg rounded-bl-sm'}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white text-[#1c1a17] border border-[#eadfc9] rounded-lg rounded-bl-sm px-3.5 py-2.5 shadow-sm">
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#c8942a] animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#c8942a] animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#c8942a] animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2 bg-[#fbf7f2] border-t border-[#eadfc9]">
              {quickReplies.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  disabled={sending}
                  className="text-[11px] font-sans-pro text-[#8f6417] bg-white px-3 py-1.5 border border-[#eadfc9] hover:bg-[#c8942a] hover:text-white hover:border-[#c8942a] transition-colors rounded-full disabled:opacity-60"
                >
                  {q}
                </button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 bg-white border-t border-[#eadfc9]">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Maxie anything…"
                className="flex-1 bg-[#fbf7f2] border border-[#eadfc9] px-4 py-2.5 text-sm rounded-full outline-none focus:border-[#c8942a] transition-colors"
              />
              <button type="submit" disabled={sending} className="w-11 h-11 rounded-full bg-[#0f0d0b] hover:bg-[#c8942a] text-white grid place-items-center transition-colors disabled:opacity-60" aria-label="send">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const LiveChat = () => (
  <>
    <WhatsAppWidget />
    <AiWidget />
  </>
);

export default LiveChat;
